import React from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    TooltipProps
} from 'recharts';
import moment, { Moment } from 'moment';
import { DataStackedAttributes, BaseAxisChartProps, DataFlattenedAttributes } from '../types';
import { COLORS } from '../constants';
import {
    flattenStackedGraphData,
    formatDateYYYYMMDD
} from '../utils';
import { formatNumberShort } from '../../../utils/format';
import { GraphTooltip } from '../shared/GraphTooltip';
import { DateUtil } from '../../../utils/date';

export type { DataStackedAttributes };

interface ScatterGraphStackedProps extends BaseAxisChartProps {
    data: DataStackedAttributes[];
    xAxisLabel?: string;
    yAxisLabel?: string;
    showTooltip?: boolean;
    showLegend?: boolean;
}

export const ScatterGraphStacked: React.FC<ScatterGraphStackedProps> = ({
    data,
    xAxisLabel,
    yAxisLabel,
    showXAxis = false,
    showYAxis = false,
    showTooltip = true,
    showLegend = true,
}) => {

    // Constants for configuration and styling
    const DATA_FILTER_YEARS = 5;
    const MARGIN = { top: 20, right: 20, left: 20, bottom: 20 };
    const GRID_STROKE_DASHARRAY = "3 3";
    const TICK_FONT_SIZE = 10;
    const Y_AXIS_LABEL_ANGLE = -90;
    const TOOLTIP_OFFSET = 10;

    const formatYearOnly = (value: any): string => {
        const dateMoment = moment(value);
        return dateMoment.isValid() ? dateMoment.year().toString() : String(value);
    };
    const finalXAxisFormatter = formatYearOnly;

    const fiveYearsAgoMoment: Moment = DateUtil.today();
    fiveYearsAgoMoment.subtract(DATA_FILTER_YEARS, 'years');

    const filteredData = data.filter(item => {
        try {
            const itemDateMoment = moment(item.x);
            return itemDateMoment.isValid() && itemDateMoment.isSameOrAfter(fiveYearsAgoMoment);
        } catch (e) {
            console.error("Error parsing date for filtering:", item.x, e);
            return false;
        }
    });

    const transformedData = flattenStackedGraphData(filteredData);

    const yKeys = transformedData.length > 0
        ? Object.keys(transformedData[0]).filter(key => key.startsWith('y'))
        : [];

    const labels = yKeys.map((key, index) => {
        const labelKey = `label${index}` as keyof DataFlattenedAttributes;
        return transformedData[0]?.[labelKey] ?? `Series ${index + 1}`;
    });

    const scatterTitleExtractor = (payload: any[]) => {
        if (!payload || payload.length === 0) return undefined;
        return formatDateYYYYMMDD(payload[0].payload.x);
    };

    const scatterItemsExtractor = (payload: any[]) => {
        if (!payload || payload.length === 0) return [];

        const item = payload[0];
        const yKeyIndex = yKeys.indexOf(item.dataKey);
        const label = yKeyIndex !== -1 ? labels[yKeyIndex] : item.name;

        return [{
            label: String(label),
            value: item.value,
            color: item.color || item.fill,
        }];
    };

    const scatterValueFormatter = (value: any) => {
        return formatNumberShort(value, { suffix: ' €', invalidInputReturn: '' }) ?? '';
    };

    const renderTooltip = (props: TooltipProps<number, string>) => {
        return (
            <GraphTooltip
                {...props}
                titleExtractor={scatterTitleExtractor}
                itemsExtractor={scatterItemsExtractor}
                valueFormatter={scatterValueFormatter}
            />
        );
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
                margin={MARGIN}
            >
                <CartesianGrid strokeDasharray={GRID_STROKE_DASHARRAY} vertical={false} />

                {showXAxis && (
                    <XAxis
                        type="number"
                        dataKey="x"
                        domain={['auto', 'auto']}
                        name={xAxisLabel || 'Date'}
                        tickFormatter={finalXAxisFormatter}
                        tick={{ fontSize: TICK_FONT_SIZE, fontWeight: 'bold' }}
                        axisLine={false}
                        scale="time"
                        allowDuplicatedCategory={false}
                    />
                )}

                {showYAxis && (
                    <YAxis
                        type="number"
                        dataKey="y0"
                        orientation="right"
                        name={yAxisLabel || 'Value'}
                        label={yAxisLabel ? { value: yAxisLabel, angle: Y_AXIS_LABEL_ANGLE, position: 'insideRight' } : undefined}
                        tick={{ fontSize: TICK_FONT_SIZE, fontWeight: 'bold' }}
                        tickFormatter={(value) => formatNumberShort(value, { suffix: ' €', invalidInputReturn: '' }) ?? ''}
                        axisLine={false}
                        domain={['auto', 'auto']}
                    />
                )}

                {showTooltip && (
                    <RechartsTooltip
                        content={renderTooltip}
                        position={{ x: 0, y: 0 }}
                        offset={TOOLTIP_OFFSET}
                        isAnimationActive={false}
                    />
                 )}

                {showLegend && <Legend />}

                {yKeys.map((key, index) => (
                    <Scatter
                        key={key}
                        data={transformedData}
                        dataKey={key}
                        name={String(labels[index])}
                        fill={COLORS[index % COLORS.length]}
                        isAnimationActive={false}
                    />
                ))}
            </ScatterChart>
        </ResponsiveContainer>
    );
};

export default ScatterGraphStacked;




